import React, { useState, useEffect } from 'react';
import Step3_FinalCard from '../Step3_FinalCard';
import { useAuth } from '../contexts/AuthContext';

import { getCard } from '../api/card';
import { parseCardState, serializeCardState } from '../utils/cardState';
import { sendMessage } from '../api/messages';
import { activateSession, deactivateSession } from '../api/session';

function CardViewPage({ shortId }) {
  const { user, isLoading: authLoading, cardStatus, savedCard, unreadCount } = useAuth();
  const [state, setState] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showMsgForm, setShowMsgForm] = useState(
    new URLSearchParams(window.location.search).get('msg') === '1'
  );
  const [msgText, setMsgText] = useState('');
  const [msgState, setMsgState] = useState('idle');
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [sessionViewCount, setSessionViewCount] = useState(0);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [showSessionPicker, setShowSessionPicker] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!sessionActive || !sessionExpiresAt) { setTimeLeft(''); return; }
    const update = () => {
      const diff = new Date(sessionExpiresAt) - Date.now();
      if (diff <= 0) { setSessionActive(false); setTimeLeft(''); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [sessionActive, sessionExpiresAt]);

  useEffect(() => {
    if (authLoading) return; // auth 확인 끝날 때까지 대기

    async function load() {
      setLoading(true);
      const idToken = user ? await user.getIdToken() : null;
      const card = await getCard(shortId, idToken);

      if (!card) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const parsed = parseCardState(card.urlParams);
      setState(parsed);
      setIsOwner(card.isOwner);
      setSessionActive(card.sessionActive || false);
      setSessionExpiresAt(card.sessionExpiresAt || null);
      setLoading(false);
    }

    load();
  }, [shortId, user, authLoading]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: '#999', fontSize: '0.9em' }}>명함 불러오는 중...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '12px' }}>
        <p style={{ color: '#333', fontWeight: 600 }}>존재하지 않는 명함입니다</p>
        <a href="/" style={{ color: '#666', fontSize: '0.85em' }}>내 명함 만들기 →</a>
      </div>
    );
  }

  const handleActivateSession = async (hours) => {
    if (!user) return;
    setSessionLoading(true);
    setShowSessionPicker(false);
    try {
      const idToken = await user.getIdToken();
      const data = await activateSession(shortId, hours, idToken);
      setSessionActive(data.active);
      setSessionExpiresAt(data.expiresAt);
      setSessionViewCount(data.viewCount);
    } catch (e) {
      // silent fail
    } finally {
      setSessionLoading(false);
    }
  };

  const handleDeactivateSession = async () => {
    if (!user) return;
    setSessionLoading(true);
    try {
      const idToken = await user.getIdToken();
      await deactivateSession(shortId, idToken);
      setSessionActive(false);
      setSessionExpiresAt(null);
      setSessionViewCount(0);
    } catch (e) {
      // silent fail
    } finally {
      setSessionLoading(false);
    }
  };

  const handleEditCard = () => {
    window.location.href = '/' + (state ? serializeCardState(state) : '');
  };

  const handleSendMessage = async () => {
    if (!msgText.trim() || !user) return;
    setMsgState('sending');
    try {
      const idToken = await user.getIdToken();
      const senderName = savedCard?.details?.name || user.displayName || '';
      const senderCardId = savedCard?.shortId || '';
      await sendMessage({ cardId: shortId, text: msgText.trim(), senderName, senderCardId, idToken });
      setMsgState('sent');
      setMsgText('');
      setTimeout(() => { setShowMsgForm(false); setMsgState('idle'); }, 1500);
    } catch (e) {
      setMsgState(e.message?.includes('한도') || e.message?.includes('rate') ? 'ratelimit' : 'error');
      setTimeout(() => setMsgState('idle'), 3000);
    }
  };

  return (
    <>
      <div style={{
        position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
        background: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px',
        padding: '6px 16px', fontSize: '0.8em', color: '#333',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)', zIndex: 1000,
        display: 'flex', gap: '12px', alignItems: 'center',
      }}>
        {isOwner ? (
          <>
            <span style={{ color: '#999' }}>내 명함</span>
            <button
              onClick={handleEditCard}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', fontSize: '0.9em', padding: 0 }}
            >
              편집하기 →
            </button>
            <a href="/messages" style={{
              color: '#333', textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              쪽지함
              {unreadCount > 0 && (
                <span style={{
                  background: '#000', color: '#fff', borderRadius: '10px',
                  fontSize: '0.75em', padding: '1px 6px', lineHeight: '1.4',
                }}>
                  {unreadCount}
                </span>
              )}
            </a>
            {/* 공개 세션 토글 */}
            {sessionLoading ? (
              <span style={{ fontSize: '0.8em', color: '#aaa' }}>...</span>
            ) : sessionActive ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: '#22c55e', display: 'inline-block',
                  boxShadow: '0 0 0 2px rgba(34,197,94,0.25)',
                }} />
                <span style={{ fontSize: '0.75em', color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>
                  {timeLeft}
                </span>
                <button
                  onClick={handleDeactivateSession}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '0.75em', padding: 0 }}
                >
                  종료
                </button>
              </span>
            ) : (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowSessionPicker(v => !v)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', fontSize: '0.8em', padding: 0 }}
                >
                  공유 모드
                </button>
                {showSessionPicker && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6,
                    background: '#fff', border: '1px solid #e5e5e5', borderRadius: 10,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    padding: '8px 0', zIndex: 2000, minWidth: 130,
                  }}>
                    <p style={{ fontSize: 11, color: '#999', margin: '0 0 6px', padding: '0 14px' }}>연락처 공개 시간</p>
                    {[1, 3, 6].map(h => (
                      <button
                        key={h}
                        onClick={() => handleActivateSession(h)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '7px 14px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: '0.85em', color: '#333',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        {h}시간
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : user && cardStatus === 'verified' && savedCard?.shortId ? (
          <>
            <a href={`/card/${savedCard.shortId}`} style={{ color: '#666', textDecoration: 'none' }}>
              내 명함 →
            </a>
            <a href="/messages" style={{ color: '#666', textDecoration: 'none' }}>
              쪽지함
            </a>
          </>
        ) : user && cardStatus !== 'unknown' ? (
          <a href="/" style={{ color: '#666', textDecoration: 'none' }}>
            내 명함 만들기 →
          </a>
        ) : (
          <a href="/" style={{ color: '#666', textDecoration: 'none' }}>
            나도 만들기 →
          </a>
        )}
      </div>
      <Step3_FinalCard
        {...state}
        isComplete={true}
        shortId={shortId}
        isOwner={isOwner}
        publicSessionActive={sessionActive}
        onPrev={() => window.history.back()}
        onShortIdChange={() => {}}
      />

      {/* 쪽지 보내기 버튼 (타인 명함 + 로그인) */}
      {!isOwner && user && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 999 }}>
          {!showMsgForm ? (
            <button
              onClick={() => setShowMsgForm(true)}
              style={{
                padding: '10px 24px', background: '#000', color: '#fff',
                border: 'none', borderRadius: 24, fontSize: '0.85em',
                cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}
            >
              쪽지 보내기
            </button>
          ) : (
            <div style={{
              background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12,
              padding: 16, width: 300, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            }}>
              <textarea
                value={msgText}
                onChange={e => setMsgText(e.target.value)}
                placeholder="쪽지를 남겨보세요 (최대 500자)"
                maxLength={500}
                rows={3}
                style={{
                  width: '100%', border: '1px solid #e5e5e5', borderRadius: 6,
                  padding: '8px 10px', fontSize: '0.85em', resize: 'none',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span style={{ fontSize: '0.75em', color: '#999' }}>{msgText.length}/500</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => { setShowMsgForm(false); setMsgText(''); setMsgState('idle'); }}
                    style={{ padding: '6px 14px', background: 'none', border: '1px solid #e5e5e5', borderRadius: 6, fontSize: '0.8em', cursor: 'pointer' }}
                  >
                    취소
                  </button>
                  <button
                    onClick={handleSendMessage}
                    disabled={!msgText.trim() || msgState === 'sending' || msgState === 'sent'}
                    style={{
                      padding: '6px 14px', background: '#000', color: '#fff',
                      border: 'none', borderRadius: 6, fontSize: '0.8em', cursor: 'pointer',
                      opacity: !msgText.trim() || msgState === 'sending' ? 0.5 : 1,
                    }}
                  >
                    {msgState === 'sending' ? '전송 중...' : msgState === 'sent' ? '전송 완료!' : '전송'}
                  </button>
                </div>
              </div>
              {msgState === 'error' && <p style={{ color: '#e53e3e', fontSize: '0.78em', margin: '6px 0 0' }}>전송 실패. 다시 시도해주세요.</p>}
              {msgState === 'ratelimit' && <p style={{ color: '#e53e3e', fontSize: '0.78em', margin: '6px 0 0' }}>오늘 이 명함에 보낼 수 있는 쪽지 한도를 초과했습니다.</p>}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default CardViewPage;
